/**
 * Order Download Service
 * Located: frontend/src/features/orders/services/downloadService.js
 */

import api from "../../../shared/api.jsx";
import { logError } from "../../../shared/utils/logger.js";

class OrderDownloadService {
  /**
   * Make a binary request using the shared API's authentication and error handling
   * @param {string} url - Request URL
   * @param {Object} params - Query parameters
   * @param {string} acceptHeader - Accept header for the request
   * @returns {Promise<Response>} Fetch response
   */
  async makeBinaryRequest(url, params = {}, acceptHeader = "application/pdf") {
    try {
      const response = await api.binary(url, params, acceptHeader);
      return response;
    } catch (error) {
      logError("Binary request failed", {
        url,
        params,
        acceptHeader,
        error: error.message,
        status: error.response?.status,
      });
      throw error;
    }
  }

  /**
   * Download driver pickup details PDF
   * @param {string} orderId - Order ID
   * @returns {Promise<void>}
   */
  async downloadDriverPickupPdf(orderId) {
    try {
      const url = `/orders/${orderId}/pickup/driver/pdf`;

      const response = await this.makeBinaryRequest(url, {}, "application/pdf");

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `order_${orderId || "unknown"}_pickup_driver.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      this.downloadBlob(blob, filename);
    } catch (error) {
      logError("Driver pickup PDF download failed", {
        orderId,
        error: error.message,
        status: error.response?.status,
      });
      throw error;
    }
  }

  /**
   * Download client pickup confirmation PDF
   * @param {string} orderId - Order ID
   * @returns {Promise<void>}
   */
  async downloadClientPickupPdf(orderId) {
    try {
      const url = `/orders/${orderId}/pickup/client/pdf`;

      const response = await this.makeBinaryRequest(url, {}, "application/pdf");

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `order_${orderId || "unknown"}_pickup_client.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      this.downloadBlob(blob, filename);
    } catch (error) {
      logError("Client pickup PDF download failed", {
        orderId,
        error: error.message,
        status: error.response?.status,
      });
      throw error;
    }
  }

  async downloadBillingStatementPdf(orderId) {
    try {
      const response = await this.makeBinaryRequest(
        `/orders/${orderId}/billing-statement/pdf`,
        {},
        "application/pdf"
      );
      const contentDisposition = response.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename =
        filenameMatch?.[1] ||
        `order_${orderId || "unknown"}_billing_statement.pdf`;
      const blob = await response.blob();
      this.downloadBlob(blob, filename);
    } catch (error) {
      logError("Order billing statement PDF download failed", {
        orderId,
        error: error.message,
        status: error.response?.status,
      });
      throw error;
    }
  }

  /**
   * Download blob as file
   * @param {Blob} blob - Blob to download
   * @param {string} filename - Filename for download
   */
  downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const orderDownloadService = new OrderDownloadService();

